import type { TrackerCopy } from './types'

/** 日本語版サンタトラッカーのコンテンツ。 */
export const TRACKER_COPY_JA: TrackerCopy = {
  meta: {
    title: 'サンタクロースは今どこ？クリスマスのカウントダウンと世界地図',
    description:
      'クリスマスまであと何日か、サンタクロースがどこを飛んでいるか、何時ごろ到着するかを確認しましょう。世界地図で旅を追いかけられます。',
    keywords: [
      'サンタクロース 今どこ',
      'クリスマス カウントダウン',
      'サンタ 何時に来る',
      'サンタクロース 追跡',
      'サンタクロース 地図',
      'サンタの旅',
      'クリスマスまであと何日',
      'サンタトラッカー',
      'サンタ どこを飛んでいる',
    ],
    schemaName: 'サンタクロースの世界地図とカウントダウン',
  },

  hero: {
    badge: {
      preparing: '北極の工房',
      liftoff: '最後の準備中',
      traveling: 'プレゼントを配達中',
      completed: 'ミッション完了',
    },
    h1: {
      preparing: 'サンタクロースが来るまであとどのくらい？',
      liftoff: 'サンタクロースがもうすぐ出発します',
      traveling: 'サンタクロースは今どこにいる？',
      completed: 'サンタクロースは北極に帰りました',
    },
    intro: {
      preparing:
        '北極は大忙しです。妖精たちはプレゼントを包み、トナカイは練習し、大きなリストを何度も確認しています。クリスマスまでの時間と準備の様子をここで見てみましょう。',
      liftoff:
        '一年でいちばん長い旅まで、あと数時間。そりには荷物が積まれ、ルドルフの鼻は輝き、北極では誰も眠っていません。',
      traveling:
        '今日はクリスマスイブ。そりは世界中を飛び回っています。地図で旅の道のりを見たり、どこを通り過ぎたか、あなたの地域にはいつ近づくかを確認したりできます。',
      completed:
        'プレゼントはすべて届けられ、トナカイは北極で休んでいます。次のクリスマスの準備はもう始まっています。ここでカウントダウンを確認しましょう。',
    },
    toMap: '地図を見る',
  },

  countdown: {
    heading: {
      preparing: 'サンタクロースがあなたの地域に来るまで',
      liftoff: 'そりが出発するまで',
      traveling: 'あなたの地域に到着するまで',
      completed: '次のクリスマスまで',
    },
    units: { days: '日', hours: '時間', minutes: '分', seconds: '秒' },
    christmasNote: 'クリスマスは12月25日です。あと{days}日。',
    christmasToday: '今日はクリスマスです！',
    arrivingNow: '今、あなたの地域の上空を飛んでいます！',
    ariaLabel: 'サンタクロースの到着までのカウントダウン',
  },

  status: {
    preparing: {
      far: [
        '妖精たちは今年最初のおもちゃをもう準備しています。',
        'サンタクロースは大きなリスト用に新しいノートを開きました。',
        '工房では次のクリスマスのプレゼント案を試しています。',
        'トナカイたちは休暇から少しずつ戻っています。',
      ],
      months: [
        '北極の工房は休むことなく働いています。',
        'サンタクロースは大きなリストを見直しています。',
        'トナカイたちは練習を始めました。',
        '世界中から最初の手紙が届き始めています。',
      ],
      weeks: [
        'クラウス夫人は工房のみんなのクッキーを焼いています。',
        'サンタクロースはリストを二度目に確認しています。',
        '妖精たちは大急ぎでプレゼントを包んでいます。',
        'そりは工房で点検を待っています。',
      ],
      days: [
        'そりの出発まで、もう少しです！',
        '妖精たちは最後の袋を閉じています。',
        'ルドルフは大切な夜に向けて練習中です。',
        '北極ではもう誰も眠っていません。',
      ],
      hours: [
        'サンタクロースは最後のプレゼントを積み込んでいます。',
        'そりは出発の準備ができています。',
        'トナカイたちはつながれて、合図を待っています。',
        'ルドルフの鼻はもう輝いています。',
      ],
    },
    liftoff: [
      'サンタクロースは最後のプレゼントを積み込んでいます。',
      'ルドルフの鼻はもう輝いています。',
      '妖精たちは大きな袋を閉じています。',
      'そりは出発の準備ができています。',
    ],
    traveling: [
      'そりはたった今、{city}を通過しました。',
      'サンタクロースは袋を少し軽くして{city}を出発しました。',
      'ルドルフは{city}の停車を終えたそりを案内しています。',
      '{city}にはもうプレゼントが届きました。',
    ],
    completed: [
      'サンタクロースは旅を終えました。',
      'プレゼントはすべて届けられました。',
      'トナカイたちは北極で休んでいます。',
      '次のクリスマスの準備がもう始まっています。',
    ],
    lastVisited: '最後に訪れた場所',
    nextStop: '次の目的地',
    flyingTo: '{leg}へ飛行中',
    atNorthPole: '北極にいます',
    backHome: '家に帰りました',
    legLabel: {
      'north-pole': '北極',
      pacific: '太平洋',
      oceania: 'オセアニア',
      'east-asia': '東アジア',
      'southeast-asia': '東南アジア',
      'central-asia': '中央・南アジア',
      'middle-east': '中東',
      'eastern-europe': '東ヨーロッパ',
      africa: 'アフリカ',
      'western-europe': '西ヨーロッパ',
      'south-america': '南アメリカ',
      'north-america': '北アメリカ',
      'north-pole-return': '北極',
    },
  },

  map: {
    heading: 'サンタクロースの旅',
    subheading: 'そりや都市をタップすると、詳しく見ることができます。',
    ariaLabel: 'サンタクロースの旅を示す世界地図',
    sleighLabel: 'サンタクロースのそり',
    northPoleLabel: '北極',
    legend: { visited: '通過済み', current: 'ここにいます', upcoming: 'これから' },
    stopStatus: {
      visited: 'サンタクロースはここを通過しました',
      current: 'サンタクロースは今ここにいます',
      upcoming: 'サンタクロースはまだ到着していません',
    },
    localTime: '現地時間：{time}',
    close: '閉じる',
    followSleigh: 'そりを追いかける',
    wholeWorld: '世界全体を見る',
    listHeading: '旅を順番に見る',
    listIntro: '地図が見づらいときや、一覧で読みたいときのために、同じ旅をリストで表示します。',
    parentsNote:
      '保護者の方へ：これはシミュレーションによるクリスマス体験です。ルートはタイムゾーンをもとに計算しており、実際の位置やGPSによる追跡ではありません。',
  },

  eta: {
    heading: 'サンタクロースは何時に来る？',
    body: 'サンタクロースはおよそ{start}から{end}の間にあなたの地域へ到着します。',
    zone: 'あなたのタイムゾーン（{zone}）をもとに計算しています。',
    remaining: 'あと{countdown}です。',
    passed: 'サンタクロースはもうあなたの地域を通過しました。ツリーの下を見てみて！',
    unknownZone: 'お使いの端末の時刻をもとに計算しています。',
    disclaimer: '目安の時間であり、到着を保証するものではありません。サンタクロースはみんなが眠ってから訪れるのが好きなのです。',
  },

  stats: {
    heading: {
      preparing: '今、工房では',
      liftoff: '大切な夜の準備は完了',
      traveling: '旅を数字で見ると',
      completed: '今回の旅の記録',
    },
    labels: {
      giftsReady: '準備したプレゼント',
      giftsDelivered: '届けたプレゼント',
      lettersReceived: '受け取った手紙',
      elvesWorking: '働いている妖精',
      reindeerTraining: '練習中のトナカイ',
      kilometers: '移動距離',
      citiesVisited: '訪れた都市',
      countriesVisited: '訪れた国',
      homesWaiting: '待っている家',
      travelMinutes: '飛行時間',
    },
    units: { kilometers: 'km', hours: '時間' },
  },

  workshop: {
    heading: '今、北極では',
    activities: {
      preparing: [
        '妖精たちはプレゼントを包んでいます。',
        'サンタクロースは手紙を読んでいます。',
        'トナカイたちは出発場所で練習しています。',
        'クラウス夫人はクッキーを焼いています。',
        'そりは年に一度の点検を受けています。',
        '大きなリストの部屋で名前を確認しています。',
      ],
      liftoff: [
        'そりは最後の点検を受けています。',
        'ルドルフは大切な夜に向けて練習しています。',
        '妖精たちは袋をそりに積み込んでいます。',
        'クラウス夫人は旅のためのホットチョコレートを作っています。',
      ],
      traveling: [
        '妖精たちは管制塔から旅を見守っています。',
        'クラウス夫人は帰りを待って暖炉に火を入れています。',
        '工房では最後の包装紙を片づけています。',
        '交代が必要になったときのため、予備のトナカイが待機しています。',
      ],
      completed: [
        'トナカイたちは北極の厩舎で休んでいます。',
        'クラウス夫人は旅のあとのチョコレートを用意しています。',
        '妖精たちは来年に向けて工房を片づけています。',
        'サンタクロースは大きなリストを次のクリスマスまでしまいます。',
      ],
    },
    night: [
      '北極は夜。工房では明かりを少し落として作業しています。',
      '夜勤の妖精たちは静かにプレゼントを包んでいます。',
      'サンタクロースは大切な日に備えて少し眠っています。',
      '工房の周りでは雪の音だけが聞こえます。',
    ],
  },

  surprises: {
    northPole: 'オーロラを目覚めさせました！妖精たちが工房から手を振っています。',
    celebration: '旅が終わりました！メリークリスマス。',
  },

  controls: {
    soundOn: '音をオンにする',
    soundOff: '音をオフにする',
    fullscreenEnter: '全画面表示',
    fullscreenExit: '全画面表示を終了',
    share: '共有',
    shareCopied: 'リンクをコピーしました',
    motionOn: 'アニメーションをオンにする',
    motionOff: 'アニメーションを減らす',
  },

  share: {
    title: 'サンタクロースは今どこ？',
    text: {
      preparing: 'サンタクロースが来るまであとどのくらいか見てみましょう。',
      liftoff: 'サンタクロースがもうすぐ出発します！',
      traveling: 'サンタクロースがどこへ向かっているか見てみましょう！',
      completed: 'サンタクロースは旅を終えました。メリークリスマス！',
    },
  },

  faq: {
    heading: 'よくある質問',
    items: [
      {
        question: 'クリスマスまであとどのくらいですか？',
        answer:
          'このページのカウントダウンが秒単位で知らせてくれます。クリスマスは12月25日で、サンタクロースは24日の夜に北極を出発します。',
      },
      {
        question: 'サンタクロースは今どこにいますか？',
        answer:
          '季節によって変わります。一年の大半は北極でクリスマスの準備をし、クリスマスイブには世界中を旅します。このページの地図では、その時刻にいるはずの場所を表示します。',
      },
      {
        question: 'サンタクロースはどこを通りますか？',
        answer:
          '太平洋から始まり、オセアニア、アジア、中東、ヨーロッパ、アフリカを通り、南アメリカと北アメリカを訪れて北極に戻ります。夜が来た地域へ、西に向かって進みます。',
      },
      {
        question: 'サンタクロースはいつ北極を出発しますか？',
        answer:
          '12月24日の午後、太平洋の島々でほぼ真夜中になるころに出発します。そこから25日の朝まで、休まず世界を回ります。',
      },
      {
        question: 'サンタクロースは何時に来ますか？',
        answer:
          'どの家にも、子どもたちが眠っている時間に来ます。おおよそ21時から深夜の間です。このページでは、あなたのタイムゾーンに合わせた目安を表示します。',
      },
      {
        question: 'サンタクロースはすべての国を訪れますか？',
        answer:
          'はい。世界195か国が旅のルートに含まれています。地図上では目印としていくつかの都市を表示していますが、そりは本当に世界中を飛び回っています。',
      },
      {
        question: 'どうして一晩で世界を回れるのですか？',
        answer:
          '夜に合わせて西へ進むからです。地球の自転と同じ方向に飛ぶことで、クリスマスイブは30時間以上続きます。そこに北極の魔法も加わります。',
      },
      {
        question: 'サンタクロースが来たときに起きていたら？',
        answer:
          'サンタクロースはとても静かです。誰かが起きていると気づいたら、道を続けてあとで戻ってきます。早く寝るのが、来てもらういちばんの方法です。',
      },
      {
        question: 'サンタクロースは住んでいる場所をどうやって知るのですか？',
        answer:
          '手紙のおかげです。北極に届いた手紙は、名前と都市を大きなリストに記録し、妖精たちがその情報をもとにルートを準備します。',
      },
      {
        question: 'サンタクロースはいつ北極に帰りますか？',
        answer:
          '最後のプレゼントを届けた12月25日の朝に帰ります。トナカイは数日休み、工房では次のクリスマスの準備が始まります。',
      },
      {
        question: 'この地図はサンタクロースの実際の位置を表示していますか？',
        answer:
          'いいえ。これはシミュレーションによるクリスマス体験です。各地域の夜に合うようタイムゾーンからルートを計算しています。実際の位置やGPS追跡ではなく、位置情報も求めません。',
      },
      {
        question: 'サンタクロースと話すにはどうすればいいですか？',
        answer:
          'PapaNoel.chatでは、お子さまの名前を呼びながらリアルタイムで話す通話を準備できます。あらかじめ知っておいてほしいことは、保護者の方が決められます。',
      },
      {
        question: 'サンタクロースとビデオ通話もできますか？',
        answer:
          'はい。音声通話だけでなく、お子さまの名前、好きなこと、頑張ったこと、願いごとを知っているサンタクロースとのパーソナライズされたビデオ通話も利用できます。',
      },
    ],
  },

  cta: {
    heading: 'サンタクロースを待つ間に、話してみませんか？',
    body: 'お子さまの名前、好きなこと、頑張ったこと、願いごとを知っているサンタクロースとのビデオ通話を準備できます。',
    primary: 'サンタクロースと話す',
    secondary: '魔法のキャラクターを見る',
    free: 'まずは試してみたい方は、サンタクロースと無料で話そう',
    travelingNote: '今夜のサンタクロースは大忙しですが、魔法のビデオ通話を準備することはできます。',
  },

  stops: {
    'north-pole': '北極',
    'north-pole-return': '北極',
    auckland: 'オークランド',
    suva: 'スバ',
    sydney: 'シドニー',
    melbourne: 'メルボルン',
    brisbane: 'ブリスベン',
    'port-moresby': 'ポートモレスビー',
    darwin: 'ダーウィン',
    tokyo: '東京',
    seoul: 'ソウル',
    shanghai: '上海',
    beijing: '北京',
    'hong-kong': '香港',
    manila: 'マニラ',
    singapore: 'シンガポール',
    jakarta: 'ジャカルタ',
    bangkok: 'バンコク',
    dhaka: 'ダッカ',
    kathmandu: 'カトマンズ',
    'new-delhi': 'ニューデリー',
    karachi: 'カラチ',
    kabul: 'カブール',
    dubai: 'ドバイ',
    tehran: 'テヘラン',
    riyadh: 'リヤド',
    istanbul: 'イスタンブール',
    moscow: 'モスクワ',
    athens: 'アテネ',
    warsaw: 'ワルシャワ',
    cairo: 'カイロ',
    johannesburg: 'ヨハネスブルグ',
    lagos: 'ラゴス',
    berlin: 'ベルリン',
    rome: 'ローマ',
    paris: 'パリ',
    madrid: 'マドリード',
    london: 'ロンドン',
    lisbon: 'リスボン',
    reykjavik: 'レイキャビク',
    'rio-de-janeiro': 'リオデジャネイロ',
    'sao-paulo': 'サンパウロ',
    'buenos-aires': 'ブエノスアイレス',
    santiago: 'サンティアゴ',
    'la-paz': 'ラパス',
    caracas: 'カラカス',
    bogota: 'ボゴタ',
    lima: 'リマ',
    'new-york': 'ニューヨーク',
    toronto: 'トロント',
    havana: 'ハバナ',
    'mexico-city': 'メキシコシティ',
    chicago: 'シカゴ',
    denver: 'デンバー',
    'los-angeles': 'ロサンゼルス',
    vancouver: 'バンクーバー',
    anchorage: 'アンカレッジ',
    honolulu: 'ホノルル',
  },
}
